import { globalState } from '../global-state';
import {
  DefinitionKeyValue,
  ReferenceDefinition,
} from './reference-definitions';
import { missingTermMarker, resolveTermPath } from './term-resolution';
import { isPersonTag } from './person-tag';

/**
 * The namespace word that routes a tag to the typed `terms` object rather than to the legacy
 * reference registry. `{{term loginGroup.value}}` is `terms.loginGroup.value` with the object root
 * swapped for this word, so code and stored data share one mental model.
 */
const TERM_NAMESPACE = 'term';

/**
 * The namespace word of a literal tag, `{{text <base64url>}}`: text a person typed that the server
 * put into kept text (server `LiteralTag.For`, #398 C10). It renders as exactly those characters
 * and is never scanned for tags, so a name typed as `{{person <id>}}` or `{{term ...}}` reads as
 * what was typed rather than as a person's card or a word.
 */
const LITERAL_NAMESPACE = 'text';

/**
 * The characters a literal tag stands for, or undefined when its payload is not base64url of UTF-8.
 * The server writes no padding and the URL-safe alphabet, so both are undone here.
 */
export function decodeLiteral(payload: string): string | undefined {
  if (!/^[A-Za-z0-9_-]+$/.test(payload)) return undefined;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return undefined;
  }
}

export class ReferenceItem implements ReferenceDefinition {
  name: string;
  description: string;
  value: string;
  other: DefinitionKeyValue[];
  constructor(
    name: string,
    description: string,
    value: string,
    other: DefinitionKeyValue[]
  ) {
    this.name = name;
    this.description = description;
    this.value = value;
    this.other = other;
  }
  add(key: string, value: string, description?: string): ReferenceDefinition {
    if (this.other.some((item) => item.key === key)) {
      throw new Error(`Key "${key}" already exists.`);
    }
    this.other.push({ key: key, value: value, description: description });
    return this;
  }
}

globalState().references = globalState().references ?? [];

export class ReferenceManager {
  static instance: ReferenceManager = new ReferenceManager();
  references: ReferenceDefinition[] = [];
  constructor() {
    this.references = globalState().references as ReferenceDefinition[];
  }
  addReference(reference: ReferenceDefinition) {
    this.references.push(reference);

    return reference;
  }

  /**
   * Resolves a reference, or returns undefined when it does not exist.
   *
   * Both miss paths return undefined rather than a plausible string. Previously an unknown KEY fell
   * through to the reference's own value - which for the glossary was the literal word "glossary",
   * so a typo rendered that word onto the screen - and an unknown NAME returned the empty string, so
   * the text silently vanished. Those were the two quietest ways to be wrong during a rename
   * (D-028). Callers turn a miss into a visible marker.
   */
  lookup(name: string, other?: string): string | undefined {
    const reference = this.references.find((item) => item.name === name);
    if (!reference) return undefined;

    if (other) {
      return reference.other.find((item) => item.key === other)?.value;
    }
    return reference.value;
  }

  getValue(name: string, other?: string): string {
    return (
      this.lookup(name, other) ??
      missingTermMarker(other ? `${name} ${other}` : name)
    );
  }
}

export const referenceManager = ReferenceManager.instance;

export function addReference(
  name: string,

  value: string,
  description?: string,
  other?: DefinitionKeyValue[]
): ReferenceDefinition {
  return referenceManager.addReference(
    new ReferenceItem(name, description ?? '', value, other ?? [])
  );
}

export function getReference(name: string, other?: string): string {
  if (other === undefined && name.includes(' ')) {
    const [mainName, otherName] = name.split(' ');
    name = mainName.trim();
    other = otherName?.trim();
  }
  return referenceManager.getValue(name, other);
}

/**
 * Resolves every `{{...}}` tag in a string that arrived at runtime.
 *
 * Three namespaces share the syntax. `{{term key.variant}}` resolves against the typed `terms`
 * object; `{{text <base64url>}}` is typed text the server marked inert (LITERAL_NAMESPACE); anything
 * else falls through to the legacy reference registry, so existing links and branding
 * (`{{app displayName}}`, `{{company}}`) keep working untouched.
 *
 * An unresolved tag renders a marker and logs an error. It never throws: stored text can be
 * historically stale, old rows are deliberately not rewritten (D-027), and one retired key must not
 * take out the page it appears on.
 */
export function replaceReferences(input: string): string {
  // Regex breakdown:
  // {{         - Match opening braces
  // \s*        - Match optional whitespace
  // ([.\w-]+)  - Capture group 1: the namespace or reference name
  // (?:        - Non-capturing group for the optional second token
  //   \s+      - Match whitespace
  //   ([.\w-]+) - Capture group 2: the key, or the dotted `key.variant` path for a term
  // )?         - Make the second token optional
  // \s*        - Match optional whitespace
  // }}         - Match closing braces
  //
  // NOTE: there is no literal `ref` token, and a three-token form such as `{{ref term x.y}}` does
  // not match at all - it renders the braces onto the screen. A comment here previously claimed
  // otherwise, and the design document inherited the error.
  const referenceRegex = /{{\s*([.\w-]+)(?:\s+([.\w-]+))?\s*}}/g;

  return input.replace(
    referenceRegex,
    (match: string, name: string, other?: string) => {
      // A person tag (server PersonTag.For, D-162) is not a term: it is left exactly as it stands
      // for the kept-text renderer, which shows the person's current name (renderKeptText). Without
      // this, a refusal naming a person (#406, @LOCK-068) reached the page as a MISSING TERM marker,
      // because every failure passes through here. Only a well-formed person tag: one with no id or
      // a bad id, and every other unknown namespace, is still marked missing below.
      if (isPersonTag(match)) return match;
      // A literal is decoded and returned as it stands. String.replace never rescans what a
      // replacement returns, so a tag inside the typed text is not resolved.
      if (name === LITERAL_NAMESPACE) {
        const typed = other === undefined ? undefined : decodeLiteral(other);
        if (typed !== undefined) return typed;
        const path = `${LITERAL_NAMESPACE} ${other ?? ''}`.trim();
        reportUnresolved(path);
        return missingTermMarker(path);
      }
      if (name === TERM_NAMESPACE) {
        const path = other ?? '';
        const resolved = resolveTermPath(path);
        if (resolved !== undefined) return resolved;
        reportUnresolved(path);
        return missingTermMarker(path);
      }

      const resolved = referenceManager.lookup(name, other);
      if (resolved !== undefined) return resolved;
      const path = other ? `${name} ${other}` : name;
      reportUnresolved(path);
      return missingTermMarker(path);
    }
  );
}

/**
 * Logs an unresolved tag so the Playwright console guard catches terms rendered inside elements a
 * text assertion would not reach. Paired with the visible marker rather than replacing it: the two
 * nets fail on different things.
 */
function reportUnresolved(path: string): void {
  console.error(
    `Unresolved term tag "${path}". The key or variant does not exist in terms.ts. ` +
      `Keys name the internal domain (loginGroup, not member) and a tag is {{term <key>.<variant>}}.`
  );
}
